import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Container, 
  Typography, 
  Box, 
  Button,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Divider,
  ListItemIcon,
  Tooltip,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Badge,
  Skeleton,
  Chip,
  Stack,
  Fade,
  Zoom,
  Slide
} from '@mui/material';
import { 
  Logout, 
  Dashboard, 
  Menu as MenuIcon,
  Subscriptions,
  ShowChart,
  Person,
  DarkMode,
  LightMode,
  Close,
  Star,
  Diamond,
  Login,
  PersonAdd
} from '@mui/icons-material';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { keyframes } from '@mui/system';

import AuthModal from './auth/AuthModal';
import authService from '../services/authService';
import subscriptionService from '../services/subscriptionService';
import NotificationToggle from './NotificationToggle';
import { ThemeToggleIcon } from '../ThemeToggle';
import { useTheme } from '../ThemeProvider';

// Constants
const LOGO_MAX_HEIGHT = 80;
const LOGO_MAX_WIDTH = 200;

// Enhanced Animations
const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 5px rgba(191, 155, 48, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(191, 155, 48, 0.6), 0 0 30px rgba(191, 155, 48, 0.4);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-2px);
  }
`;

const gradientShift = keyframes`
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
`;

// Navigation configuration
const NAVIGATION_ITEMS = [
  { 
    label: 'Dashboard', 
    path: '/dashboard', 
    icon: Dashboard, 
    authRequired: true,
    exact: true,
    gradient: 'linear-gradient(45deg, #5980a6 0%, #3f6288 100%)'
  },
  { 
    label: 'Trades', 
    path: '#trades', 
    icon: ShowChart, 
    authRequired: false,
    exact: false,
    gradient: 'linear-gradient(45deg, #5980a6 0%, #cf6f61 100%)'
  },
  { 
    label: 'Pricing', 
    path: '#pricing', 
    icon: Subscriptions, 
    authRequired: false,
    exact: false,
    gradient: 'linear-gradient(45deg, #5980a6 0%, #7ba0c4 100%)'
  }
];

const useAuth = () => {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    user: null,
    isPremium: false,
    isLoading: true
  });

  const updateAuthState = useCallback(async (forceRefresh = false) => {
    try {
      const isLoggedIn = authService.isAuthenticated();
      
      if (!isLoggedIn) {
        setAuthState({
          isAuthenticated: false,
          user: null,
          isPremium: false,
          isLoading: false
        });
        return;
      }

      let user = authService.getCurrentUser();
      
      if (forceRefresh && user) {
        try {
          user = await authService.refreshUserData();
        } catch (error) {
          console.error('Failed to refresh user data:', error);
        }
      }

      if (!user) {
        authService.logout();
        setAuthState({
          isAuthenticated: false,
          user: null,
          isPremium: false,
          isLoading: false
        });
        return;
      }

      const isPremium = subscriptionService.hasPremiumAccess();

      setAuthState({
        isAuthenticated: true,
        user,
        isPremium,
        isLoading: false
      });

    } catch (error) {
      console.error('Auth state update failed:', error);
      setAuthState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  // Listen for auth events (login, logout, update)
  useEffect(() => {
    const handleLogin = (e) => {
      const user = e.detail;
      const isPremium = user?.subscription_plan === 'standard' || user?.subscription_plan === 'premium';
      setAuthState({
        isAuthenticated: true,
        user,
        isPremium,
        isLoading: false
      });
    };

    const handleLogout = () => {
      setAuthState({
        isAuthenticated: false,
        user: null,
        isPremium: false,
        isLoading: false
      });
    };

    const handleUpdate = (e) => {
      const user = e.detail;
      const isPremium = user?.subscription_plan === 'standard' || user?.subscription_plan === 'premium';
      setAuthState(prev => ({
        ...prev,
        user,
        isPremium
      }));
    };

    window.addEventListener('auth:login', handleLogin);
    window.addEventListener('auth:logout', handleLogout);
    window.addEventListener('auth:update', handleUpdate);

    return () => {
      window.removeEventListener('auth:login', handleLogin);
      window.removeEventListener('auth:logout', handleLogout);
      window.removeEventListener('auth:update', handleUpdate);
    };
  }, []);

  // Initial auth check
  useEffect(() => {
    updateAuthState();
  }, [updateAuthState]);

  return { authState, updateAuthState };
};

const useNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigate = useCallback((path, authRequired = false, isAuthenticated = false, onAuthRequired = () => {}) => {
    if (authRequired && !isAuthenticated) {
      onAuthRequired();
      return;
    }

    const isHomePage = location.pathname === '/';

    if (path.startsWith('#')) {
      if (!isHomePage) {
        navigate('/' + path);
      } else {
        const element = document.getElementById(path.substring(1));
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    } else {
      navigate(path);
    }
  }, [navigate, location.pathname]);

  const isRouteActive = useCallback((path) => {
    return location.pathname === path || 
           (path !== '/' && location.pathname.startsWith(path));
  }, [location.pathname]);

  return { handleNavigate, isRouteActive };
};

// Utility functions
const getUserInitials = (user) => {
  if (!user?.first_name || !user?.last_name) return '?';
  return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
};

const getSubscriptionConfig = (subscriptionPlan) => {
  const configs = {
    standard: {
      color: '#5980a6',
      gradient: 'linear-gradient(135deg, #5980a6 0%, #3f6288 100%)',
      icon: Star,
      label: 'Pro'
    },
    premium: {
      color: '#5980a6',
      gradient: 'linear-gradient(135deg, #5980a6 0%, #cf6f61 100%)',
      icon: Diamond,
      label: 'Premium'
    },
    enterprise: {
      color: '#5980a6',
      gradient: 'linear-gradient(135deg, #5980a6 0%, #7ba0c4 100%)',
      icon: Diamond,
      label: 'Enterprise'
    }
  };
  return configs[subscriptionPlan] || {
    color: '#3a3f45',
    gradient: 'linear-gradient(135deg, #3a3f45 0%, #2a2e33 100%)',
    icon: Person,
    label: 'Free'
  };
};

const Logo = React.memo(({ onClick, mode }) => (
  <Box
    onClick={onClick}
    sx={{
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      '&:hover': {
        '& .logo-text': {
          animation: `${glow} 2s ease-in-out infinite`,
        },
        '& .logo-image': {
          animation: `${float} 1s ease-in-out infinite`,
          filter: 'drop-shadow(0 0 8px rgba(89, 128, 166, 0.5))'
        }
      }
    }}
  >
    <Box
      className="logo-image"
      sx={{
        mr: 1.25,
        display: 'flex',
        alignItems: 'center',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <img
        src="logo-mark.png"
        alt="XAUPilot Logo"
        style={{
          height: 34,
          width: 'auto',
          maxHeight: LOGO_MAX_HEIGHT,
          maxWidth: LOGO_MAX_WIDTH,
          objectFit: 'contain',
          transition: 'all 0.3s ease'
        }}
        loading="lazy"
      />
    </Box>
    <Typography 
      className="logo-text"
      variant="h5" 
      component="div" 
      sx={{ 
        fontWeight: 800,
        background: 'linear-gradient(45deg, #5980a6 30%, #7ba0c4 90%)',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundSize: '200% 200%',
        animation: `${gradientShift} 3s ease infinite`,
        userSelect: 'none',
        letterSpacing: '-0.5px',
        textShadow: 'none',
        position: 'relative',
        fontSize: { xs: '1.1rem', sm: '1.25rem' },
        lineHeight: 1,
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
          animation: `${shimmer} 3s infinite`,
          pointerEvents: 'none'
        }
      }}
    >
      XAUPilot
    </Typography>
  </Box>
));

// Enhanced Navigation Items with better light mode support
const NavigationItems = React.memo(({ items, onNavigate, isRouteActive, isAuthenticated, onAuthRequired, mode }) => (
  <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
    {items.map((item, index) => {
      const IconComponent = item.icon;
      const isActive = isRouteActive(item.path);
      return (
        <Zoom in timeout={200 + index * 100} key={item.path}>
          <Button 
            color="inherit" 
            onClick={() => onNavigate(item.path, item.authRequired, isAuthenticated, onAuthRequired)}
            sx={{ 
              position: 'relative',
              px: 2.5,
              py: 1,
              borderRadius: '2px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              overflow: 'hidden',
              background: isActive 
                ? `linear-gradient(135deg, rgba(191, 155, 48, 0.15) 0%, rgba(191, 155, 48, 0.05) 100%)`
                : 'transparent',
              backdropFilter: isActive ? 'none' : 'none',
              border: isActive ? '1px solid rgba(191, 155, 48, 0.2)' : '1px solid transparent',
              color: isActive 
                ? '#5980a6' 
                : mode === 'light' 
                  ? 'rgba(0, 0, 0, 0.87)' 
                  : 'rgba(255, 255, 255, 0.87)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: item.gradient || 'linear-gradient(90deg, transparent, rgba(191, 155, 48, 0.1), transparent)',
                transition: 'left 0.5s ease',
              },
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                background: `linear-gradient(135deg, rgba(191, 155, 48, 0.1) 0%, rgba(191, 155, 48, 0.05) 100%)`,
                backdropFilter: 'none',
                border: '1px solid rgba(191, 155, 48, 0.3)',
                color: '#5980a6',
                '&::before': {
                  left: '100%',
                }
              }
            }}
            startIcon={
              <IconComponent 
                fontSize="small" 
                sx={{ 
                  filter: isActive ? 'drop-shadow(0 0 4px rgba(191, 155, 48, 0.5))' : 'none',
                  transition: 'all 0.3s ease'
                }} 
              />
            }
          >
            {item.label}
          </Button>
        </Zoom>
      );
    })}
  </Box>
));

// Enhanced Mobile Drawer with proper auth buttons
const MobileDrawer = React.memo(({ 
  open, 
  onClose, 
  items, 
  onNavigate, 
  isRouteActive, 
  isAuthenticated, 
  user, 
  mode, 
  onLogout,
  onAuthRequired,
  subscriptionConfig,
  onLogin,
  onSignup
}) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: { 
        width: 320,
        background: mode === 'light'
          ? 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)'
          : 'linear-gradient(135deg, rgba(18,18,18,0.95) 0%, rgba(18,18,18,0.85) 100%)',
        backdropFilter: 'none',
        border: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}`
      }
    }}
  >
    <Slide direction="left" in={open} mountOnEnter unmountOnExit>
      <Box sx={{ width: 320 }} role="presentation">
        {/* Enhanced Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: 2,
          background: 'linear-gradient(135deg, #5980a6 0%, #7ba0c4 100%)',
          color: 'white'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Menu
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </Box>

        <List sx={{ p: 2 }}>
          {/* Auth Buttons for Non-Authenticated Users */}
          {!isAuthenticated && (
            <>
              <ListItem sx={{ px: 0, pb: 2 }}>
                <Stack direction="column" spacing={1} sx={{ width: '100%' }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      onLogin();
                      onClose();
                    }}
                    startIcon={<Login />}
                    fullWidth
                    sx={{
                      borderRadius: '2px',
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      borderColor: 'rgba(191, 155, 48, 0.5)',
                      color: mode === 'light' ? '#5980a6' : '#7ba0c4',
                      background: 'rgba(191, 155, 48, 0.05)',
                      '&:hover': {
                        borderColor: '#5980a6',
                        background: 'rgba(191, 155, 48, 0.1)',
                      }
                    }}
                  >
                    Log In
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      onSignup();
                      onClose();
                    }}
                    startIcon={<PersonAdd />}
                    fullWidth
                    sx={{
                      borderRadius: '2px',
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #5980a6 0%, #7ba0c4 100%)',
                      boxShadow: '0 4px 15px rgba(191, 155, 48, 0.4)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #7ba0c4 0%, #5980a6 100%)',
                        boxShadow: '0 6px 20px rgba(191, 155, 48, 0.5)'
                      }
                    }}
                  >
                    Sign Up
                  </Button>
                </Stack>
              </ListItem>
              <Divider sx={{ my: 2, opacity: 0.3 }} />
            </>
          )}

          {/* Theme toggle with enhanced styling */}
          <ListItem sx={{
            borderRadius: '2px',
            mb: 1,
            background: 'rgba(191, 155, 48, 0.05)',
            border: '1px solid rgba(191, 155, 48, 0.1)'
          }}>
            <ListItemIcon>
              {mode === 'light' ? 
                <DarkMode sx={{ color: '#5980a6' }} /> : 
                <LightMode sx={{ color: '#5980a6' }} />
              }
            </ListItemIcon>
            <ListItemText 
              primary={`${mode === 'light' ? 'Dark' : 'Light'} Mode`}
              primaryTypographyProps={{
                color: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.87)'
              }}
            />
            <ThemeToggleIcon />
          </ListItem>
          
          <Divider sx={{ my: 2, opacity: 0.3 }} />
          
          {/* Enhanced Navigation items */}
          {items.map((item, index) => {
            const IconComponent = item.icon;
            const isActive = isRouteActive(item.path);
            return (
              <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => {
                    onNavigate(item.path, item.authRequired, isAuthenticated, onAuthRequired);
                    onClose();
                  }}
                  selected={isActive}
                  sx={{
                    borderRadius: '2px',
                    background: isActive ? item.gradient : 'transparent',
                    color: isActive 
                      ? 'white' 
                      : mode === 'light' 
                        ? 'rgba(0, 0, 0, 0.87)' 
                        : 'rgba(255, 255, 255, 0.87)',
                    '&:hover': {
                      background: isActive ? item.gradient : 'rgba(191, 155, 48, 0.1)',
                      transform: 'translateX(4px)'
                    },
                    '&.Mui-selected': {
                      background: item.gradient,
                      color: 'white',
                      '&:hover': {
                        background: item.gradient,
                      }
                    }
                  }}
                >
                  <ListItemIcon>
                    <IconComponent 
                      fontSize="small" 
                      sx={{ 
                        color: isActive ? 'white' : '#5980a6',
                        filter: isActive ? 'drop-shadow(0 0 4px rgba(255,255,255,0.5))' : 'none'
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.label} 
                    primaryTypographyProps={{ 
                      fontWeight: isActive ? 600 : 500 
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
          
          {/* Enhanced User menu items */}
          {isAuthenticated && user && (
            <>
              <Divider sx={{ my: 2, opacity: 0.3 }} />
              
              {/* User info in mobile */}
              <ListItem sx={{
                borderRadius: '2px',
                mb: 2,
                background: subscriptionConfig.gradient,
                color: 'white'
              }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    mr: 2,
                    background: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 700
                  }}
                >
                  {getUserInitials(user)}
                </Avatar>
                <ListItemText 
                  primary={`${user.first_name} ${user.last_name}`}
                  secondary={subscriptionConfig.label}
                  primaryTypographyProps={{ 
                    fontWeight: 600, 
                    color: 'white',
                    fontSize: '0.9rem'
                  }}
                  secondaryTypographyProps={{
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: '0.75rem'
                  }}
                />
              </ListItem>
              
              {[
                { icon: Person, label: 'My Profile', path: '/profile' },
                { icon: Subscriptions, label: 'Subscription', path: '/subscription' }
              ].map((menuItem) => (
                <ListItem key={menuItem.path} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton 
                    onClick={() => { 
                      onNavigate(menuItem.path, true); 
                      onClose(); 
                    }}
                    sx={{
                      borderRadius: '2px',
                      color: mode === 'light' 
                        ? 'rgba(0, 0, 0, 0.87)' 
                        : 'rgba(255, 255, 255, 0.87)',
                      '&:hover': {
                        background: 'rgba(191, 155, 48, 0.1)',
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <ListItemIcon>
                      <menuItem.icon sx={{ color: '#5980a6' }} />
                    </ListItemIcon>
                    <ListItemText primary={menuItem.label} />
                  </ListItemButton>
                </ListItem>
              ))}
              
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => { 
                    onLogout(); 
                    onClose(); 
                  }}
                  sx={{ 
                    borderRadius: '2px',
                    color: 'error.main',
                    '&:hover': {
                      background: 'rgba(244, 67, 54, 0.1)',
                      transform: 'translateX(4px)'
                    }
                  }}
                >
                  <ListItemIcon>
                    <Logout color="error" />
                  </ListItemIcon>
                  <ListItemText primary="Logout" />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>
      </Box>
    </Slide>
  </Drawer>
));

// Compact User Avatar for mobile
const CompactUserAvatar = React.memo(({ user, subscriptionConfig, onClick, mode }) => (
  <Tooltip title="Account" placement="bottom">
    <IconButton
      onClick={onClick}
      size="small"
      sx={{ 
        p: 0.5,
        '&:hover': {
          transform: 'scale(1.05)',
        },
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      }}
    >
      <Avatar 
        sx={{ 
          width: 32, 
          height: 32, 
          background: subscriptionConfig.gradient,
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'white',
          border: '2px solid rgba(191, 155, 48, 0.3)'
        }}
      >
        {getUserInitials(user)}
      </Avatar>
    </IconButton>
  </Tooltip>
));

// Enhanced Auth Buttons with better mobile support
const AuthButtons = React.memo(({ onLogin, onSignup, mode, isMobile }) => (
  <Stack direction="row" spacing={0.5} sx={{ display: { xs: 'none', sm: 'flex' } }}>
    <Button 
      variant="outlined" 
      onClick={onLogin}
      size="small"
      startIcon={<Login fontSize="small" />}
      sx={{
        borderRadius: '2px',
        px: { sm: 2, md: 3 },
        py: 1,
        fontWeight: 600,
        textTransform: 'none',
        fontSize: '0.875rem',
        borderColor: 'rgba(191, 155, 48, 0.5)',
        color: mode === 'light' ? '#5980a6' : '#7ba0c4',
        backdropFilter: 'none',
        background: 'rgba(191, 155, 48, 0.05)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          borderColor: '#5980a6',
          background: 'rgba(191, 155, 48, 0.1)',
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(191, 155, 48, 0.3)'
        }
      }}
    >
      Log In
    </Button>
    <Button 
      variant="contained"
      onClick={onSignup}
      size="small"
      startIcon={<PersonAdd fontSize="small" />}
      sx={{
        borderRadius: '2px',
        px: { sm: 2, md: 3 },
        py: 1,
        fontWeight: 600,
        textTransform: 'none',
        fontSize: '0.875rem',
        background: 'linear-gradient(135deg, #5980a6 0%, #7ba0c4 100%)',
        boxShadow: '0 4px 15px rgba(191, 155, 48, 0.4)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          background: 'linear-gradient(135deg, #7ba0c4 0%, #5980a6 100%)',
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 25px rgba(191, 155, 48, 0.5)'
        }
      }}
    >
      Sign Up
    </Button>
  </Stack>
));

// Main Header Component
const Header = () => {
  const [authModalState, setAuthModalState] = useState({
    open: false,
    mode: 'login'
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const { authState, updateAuthState } = useAuth();
  const { handleNavigate, isRouteActive } = useNavigation();
  const { mode } = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

  // Memoized values
  const subscriptionConfig = useMemo(() => 
    getSubscriptionConfig(authState.user?.subscription_plan), 
    [authState.user?.subscription_plan]
  );

  // Handle Stripe return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const sessionId = urlParams.get('session_id');
    
    if (success === 'true' && sessionId && authState.isAuthenticated) {
      setTimeout(() => updateAuthState(true), 1000);
    }
  }, [location.search, authState.isAuthenticated, updateAuthState]);

  // Handle ?auth= URL parameter for opening login/register modal
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const authParam = urlParams.get('auth');
    
    if (authParam && !authState.isAuthenticated) {
      if (authParam === 'login' || authParam === 'register') {
        setAuthModalState({ mode: authParam, open: true });
        
        // Clean up URL without reload
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [location.search, authState.isAuthenticated]);

  // Event handlers
  const handleAuthModal = useCallback((mode, open = true) => {
    setAuthModalState({ mode, open });
  }, []);

  const handleCloseAuthModal = useCallback(() => {
    setAuthModalState(prev => ({ ...prev, open: false }));
    updateAuthState();
  }, [updateAuthState]);

  const handleProfileMenuOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleProfileMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleLogout = useCallback(() => {
    authService.logout();
    updateAuthState();
    setAnchorEl(null);
    handleNavigate('/');
  }, [updateAuthState, handleNavigate]);

  const handleNavigation = useCallback((path, authRequired = false) => {
    handleNavigate(path, authRequired, authState.isAuthenticated, () => handleAuthModal('login'));
    setMobileMenuOpen(false);
    setAnchorEl(null);
  }, [handleNavigate, authState.isAuthenticated, handleAuthModal]);

  const handleMobileMenuToggle = useCallback(() => {
    setMobileMenuOpen(prev => !prev);
  }, []);

  // Enhanced Loading state with shimmer effect
  if (authState.isLoading) {
    return (
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{
          background: mode === 'light'
            ? 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)'
            : 'linear-gradient(135deg, rgba(18,18,18,0.95) 0%, rgba(18,18,18,0.85) 100%)',
          backdropFilter: 'none',
          borderBottom: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'}`
        }}
      >
        <Container>
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Skeleton 
                variant="circular" 
                width={40} 
                height={40} 
                sx={{ mr: 2, background: 'rgba(191, 155, 48, 0.1)' }} 
              />
              <Skeleton 
                variant="text" 
                width={120} 
                height={30}
                sx={{ background: 'rgba(191, 155, 48, 0.1)' }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Skeleton 
                variant="circular" 
                width={40} 
                height={40}
                sx={{ background: 'rgba(191, 155, 48, 0.1)' }}
              />
              <Skeleton 
                variant="circular" 
                width={40} 
                height={40}
                sx={{ background: 'rgba(191, 155, 48, 0.1)' }}
              />
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    );
  }

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{
          background: mode === 'light' 
            ? 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)'
            : 'linear-gradient(135deg, rgba(18,18,18,0.95) 0%, rgba(18,18,18,0.85) 100%)',
          backdropFilter: 'none',
          borderBottom: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'}`,
          transition: 'all 0.3s ease'
        }}
      >
        <Container>
          <Toolbar 
            disableGutters 
            sx={{ 
              justifyContent: 'space-between', 
              py: 1.5,
              minHeight: '70px !important'
            }}
          >
            {/* Enhanced Logo */}
            <Logo onClick={() => handleNavigate('/')} mode={mode} />
            
            {/* Desktop Navigation with proper light mode support */}
            <NavigationItems
              items={NAVIGATION_ITEMS}
              onNavigate={handleNavigation}
              isRouteActive={isRouteActive}
              isAuthenticated={authState.isAuthenticated}
              onAuthRequired={() => handleAuthModal('login')}
              mode={mode}
            />
            
            {/* Right side controls with enhanced mobile support */}
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 0.5, sm: 1 }, 
              alignItems: 'center',
              // Prevent overflow on mobile
              minWidth: 0,
              flex: '0 0 auto'
            }}>
              {/* Enhanced Notifications for authenticated users */}
              {authState.isAuthenticated && (
                <Fade in timeout={500}>
                  <Box>
                    <NotificationToggle />
                  </Box>
                </Fade>
              )}
              
              {/* Enhanced Theme toggle */}
              <Zoom in timeout={300}>
                <Box sx={{
                  '& > *': {
                    borderRadius: '2px !important',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
                    '&:hover': {
                      transform: 'scale(1.1) !important',
                      boxShadow: '0 4px 12px rgba(191, 155, 48, 0.3) !important'
                    }
                  }
                }}>
                  <ThemeToggleIcon />
                </Box>
              </Zoom>
              
              {/* Enhanced User section with mobile optimization */}
              {authState.isAuthenticated && authState.user ? (
                <Fade in timeout={600}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: { xs: 0.5, sm: 1 },
                    minWidth: 0
                  }}>
                    {/* Enhanced Premium badge for desktop only */}
                    {authState.isPremium && !isMobile && (
                      <Chip
                        icon={<subscriptionConfig.icon sx={{ fontSize: '14px !important' }} />}
                        label={subscriptionConfig.label}
                        size="small"
                        sx={{
                          background: subscriptionConfig.gradient,
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          height: 28,
                          '& .MuiChip-icon': { color: 'white' },
                          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                          animation: `${glow} 3s ease-in-out infinite`,
                          display: { xs: 'none', lg: 'flex' }
                        }}
                      />
                    )}
                    
                    {/* Compact user avatar for mobile, full for desktop */}
                    {isMobile ? (
                      <CompactUserAvatar
                        user={authState.user}
                        subscriptionConfig={subscriptionConfig}
                        onClick={handleProfileMenuOpen}
                        mode={mode}
                      />
                    ) : (
                      <UserAvatar
                        user={authState.user}
                        subscriptionConfig={subscriptionConfig}
                        onClick={handleProfileMenuOpen}
                      />
                    )}

                    {/* Enhanced User menu */}
                    <UserMenu
                      user={authState.user}
                      isPremium={authState.isPremium}
                      subscriptionConfig={subscriptionConfig}
                      onNavigate={handleNavigation}
                      onLogout={handleLogout}
                      anchorEl={anchorEl}
                      onClose={handleProfileMenuClose}
                    />
                  </Box>
                </Fade>
              ) : (
                <Fade in timeout={400}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {/* Mobile menu toggle with enhanced styling */}
                    <IconButton
                      color="inherit"
                      aria-label="open drawer"
                      edge="start"
                      onClick={handleMobileMenuToggle}
                      sx={{ 
                        mr: 0.5, 
                        display: { md: 'none' },
                        borderRadius: '2px',
                        background: 'rgba(191, 155, 48, 0.05)',
                        border: '1px solid rgba(191, 155, 48, 0.1)',
                        color: mode === 'light' ? '#5980a6' : '#7ba0c4',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          background: 'rgba(191, 155, 48, 0.1)',
                          transform: 'scale(1.05)',
                          boxShadow: '0 4px 12px rgba(191, 155, 48, 0.3)'
                        }
                      }}
                    >
                      <MenuIcon />
                    </IconButton>
                    
                    {/* Enhanced Auth buttons */}
                    <AuthButtons
                      onLogin={() => handleAuthModal('login')}
                      onSignup={() => handleAuthModal('register')}
                      mode={mode}
                      isMobile={isMobile}
                    />
                  </Box>
                </Fade>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Enhanced Mobile navigation drawer with auth buttons */}
      <MobileDrawer
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        items={NAVIGATION_ITEMS}
        onNavigate={handleNavigation}
        isRouteActive={isRouteActive}
        isAuthenticated={authState.isAuthenticated}
        user={authState.user}
        mode={mode}
        onLogout={handleLogout}
        onAuthRequired={() => handleAuthModal('login')}
        subscriptionConfig={subscriptionConfig}
        onLogin={() => handleAuthModal('login')}
        onSignup={() => handleAuthModal('register')}
      />

      {/* Enhanced Auth Modal with backdrop blur */}
      <AuthModal 
        open={authModalState.open} 
        onClose={handleCloseAuthModal} 
        initialMode={authModalState.mode}
        PaperProps={{
          sx: {
            borderRadius: '2px',
            background: mode === 'light'
              ? 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)'
              : 'linear-gradient(135deg, rgba(18,18,18,0.95) 0%, rgba(18,18,18,0.85) 100%)',
            backdropFilter: 'none',
            border: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}`,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }
        }}
      />
    </>
  );
};

// Enhanced User Avatar Component (original but with mobile optimizations)
const UserAvatar = React.memo(({ user, subscriptionConfig, onClick }) => {
  const IconComponent = subscriptionConfig.icon;
  
  return (
    <Tooltip 
      title="Account settings" 
      placement="bottom"
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
    >
      <IconButton
        onClick={onClick}
        size="small"
        sx={{ 
          ml: 1,
          position: 'relative',
          '&:hover': {
            transform: 'scale(1.05)',
            '& .avatar-glow': {
              opacity: 1,
              transform: 'scale(1.2)',
            }
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
        aria-controls="profile-menu"
        aria-haspopup="true"
      >
        <Box
          className="avatar-glow"
          sx={{
            position: 'absolute',
            top: -2,
            left: -2,
            right: -2,
            bottom: -2,
            borderRadius: '50%',
            background: subscriptionConfig.gradient,
            opacity: 0,
            transform: 'scale(1)',
            transition: 'all 0.3s ease',
            zIndex: 0
          }}
        />
        <Badge
          badgeContent={
            subscriptionConfig.label !== 'Free' ? (
              <IconComponent sx={{ fontSize: 12, color: 'white' }} />
            ) : null
          }
          color="primary"
          sx={{
            '& .MuiBadge-badge': {
              background: subscriptionConfig.gradient,
              minWidth: 18,
              height: 18,
              borderRadius: '50%',
              border: '2px solid white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
            },
          }}
        >
          <Avatar 
            sx={{ 
              width: 36, 
              height: 36, 
              background: subscriptionConfig.gradient,
              fontSize: '0.875rem',
              fontWeight: 700,
              color: 'white',
              border: '2px solid rgba(255,255,255,0.2)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              position: 'relative',
              zIndex: 1
            }}
          >
            {getUserInitials(user)}
          </Avatar>
        </Badge>
      </IconButton>
    </Tooltip>
  );
});

// Enhanced User Menu Component (original but with mode support)
const UserMenu = React.memo(({ 
  user, 
  isPremium, 
  subscriptionConfig, 
  onNavigate, 
  onLogout, 
  anchorEl, 
  onClose 
}) => (
  <Menu
    id="profile-menu"
    anchorEl={anchorEl}
    open={Boolean(anchorEl)}
    onClose={onClose}
    TransitionComponent={Zoom}
    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    PaperProps={{
      elevation: 0,
      sx: {
        overflow: 'visible',
        mt: 1.5,
        minWidth: 280,
        borderRadius: '2px',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
        backdropFilter: 'none',
        border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        '&:before': {
          content: '""',
          display: 'block',
          position: 'absolute',
          top: 0,
          right: 14,
          width: 10,
          height: 10,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderBottom: 'none',
          borderRight: 'none',
          transform: 'translateY(-50%) rotate(45deg)',
          zIndex: 0,
        },
      },
    }}
  >
    {/* Enhanced User Info Header */}
    <MenuItem 
      disabled 
      sx={{ 
        opacity: '1 !important',
        background: subscriptionConfig.gradient,
        color: 'white',
        borderRadius: '2px',
        m: 1,
        mb: 0,
        '&:hover': { background: subscriptionConfig.gradient }
      }}
    >
      <Avatar
        sx={{
          width: 48,
          height: 48,
          mr: 2,
          background: 'rgba(255,255,255,0.2)',
          color: 'white',
          fontWeight: 700,
          fontSize: '1.1rem'
        }}
      >
        {getUserInitials(user)}
      </Avatar>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white' }}>
          {user.first_name} {user.last_name}
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block' }}>
          {user.email}
        </Typography>
        <Chip
          label={subscriptionConfig.label}
          size="small"
          icon={<subscriptionConfig.icon sx={{ fontSize: '14px !important', color: 'white' }} />}
          sx={{
            mt: 0.5,
            height: 20,
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: 'white',
            fontSize: '0.7rem',
            fontWeight: 600,
            '& .MuiChip-icon': { color: 'white' }
          }}
        />
      </Box>
    </MenuItem>
    
    <Box sx={{ p: 1 }}>
      {/* Menu Items with Enhanced Styling */}
      {[
        { icon: Person, label: 'My Profile', path: '/profile' },
        { icon: Dashboard, label: 'Dashboard', path: '/dashboard' },
        { icon: Subscriptions, label: 'Subscription', path: '/subscription' },
      ].map((menuItem, index) => (
        <MenuItem 
          key={menuItem.path}
          onClick={() => onNavigate(menuItem.path, true)}
          sx={{
            borderRadius: '8px',
            my: 0.5,
            transition: 'all 0.2s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(191, 155, 48, 0.1) 0%, rgba(191, 155, 48, 0.05) 100%)',
              transform: 'translateX(4px)'
            }
          }}
        >
          <ListItemIcon>
            <menuItem.icon fontSize="small" sx={{ color: '#5980a6' }} />
          </ListItemIcon>
          <ListItemText 
            primary={menuItem.label}
            primaryTypographyProps={{ fontWeight: 500 }}
          />
        </MenuItem>
      ))}
      
      <Divider sx={{ my: 1, opacity: 0.3 }} />
      
      <MenuItem 
        onClick={onLogout} 
        sx={{ 
          borderRadius: '8px',
          color: 'error.main',
          '&:hover': {
            background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(244, 67, 54, 0.05) 100%)',
            transform: 'translateX(4px)'
          }
        }}
      >
        <ListItemIcon>
          <Logout fontSize="small" color="error" />
        </ListItemIcon>
        <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 500 }} />
      </MenuItem>
    </Box>
  </Menu>
));

export default Header;